import { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { adminNavItems, getRoleDisplayName, hasPermission, AdminSection } from '@/lib/permissions';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  FileText,
  Layout,
  CreditCard,
  Plug,
  Settings,
  LogOut,
  Menu,
  Scale,
  ChevronLeft,
} from 'lucide-react';

// Icon mapping
const iconMap: Record<string, any> = {
  LayoutDashboard,
  Calendar,
  ClipboardList,
  FileText,
  Layout,
  CreditCard,
  Plug,
  Settings,
};

const AdminLayout = () => {
  const location = useLocation();
  const { signOut, user, role } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    toast.success('Signed out successfully');
  };

  // Filter navigation items based on user permissions
  const accessibleNavItems = adminNavItems.filter(item => 
    hasPermission(role, item.section as AdminSection)
  );

  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email.substring(0, 2).toUpperCase();
  };

  const SidebarContent = ({ mobile = false }: { mobile?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn(
        "p-4 border-b flex items-center gap-3",
        sidebarCollapsed && !mobile ? "justify-center" : ""
      )}>
        <Scale className="h-8 w-8 text-primary flex-shrink-0" />
        {(!sidebarCollapsed || mobile) && (
          <div className="overflow-hidden">
            <h1 className="font-bold text-lg leading-tight">Daniel Albert</h1>
            <p className="text-xs text-muted-foreground">Law Firm Admin</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {accessibleNavItems.map((item) => {
          const Icon = iconMap[item.icon] || LayoutDashboard;
          const isActive = location.pathname === item.href || 
            (item.href !== '/admin/dashboard' && location.pathname.startsWith(item.href));

          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => mobile && setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
                sidebarCollapsed && !mobile ? "justify-center" : ""
              )}
              title={sidebarCollapsed && !mobile ? item.title : undefined}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {(!sidebarCollapsed || mobile) && <span>{item.title}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className={cn(
        "p-4 border-t",
        sidebarCollapsed && !mobile ? "flex flex-col items-center gap-2" : ""
      )}>
        {(!sidebarCollapsed || mobile) && (
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                {getUserInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="overflow-hidden">
              <p className="text-sm font-medium truncate">{user?.email}</p>
              <p className="text-xs text-muted-foreground">{getRoleDisplayName(role)}</p>
            </div>
          </div>
        )}
        <Button
          variant="ghost"
          size={sidebarCollapsed && !mobile ? "icon" : "sm"}
          onClick={handleSignOut}
          className={cn(
            "text-muted-foreground hover:text-foreground",
            sidebarCollapsed && !mobile ? "" : "w-full justify-start gap-2"
          )}
          title="Sign Out"
        >
          <LogOut className="h-4 w-4" />
          {(!sidebarCollapsed || mobile) && <span>Sign Out</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-muted/30">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex flex-col border-r bg-background transition-all duration-300",
        sidebarCollapsed ? "w-20" : "w-64"
      )}>
        <SidebarContent />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute bottom-20 left-0 ml-2 lg:ml-auto transform translate-x-full lg:translate-x-0 lg:right-0 lg:left-auto lg:-mr-4 z-10 rounded-full border bg-background shadow-sm"
        >
          <ChevronLeft className={cn(
            "h-4 w-4 transition-transform",
            sidebarCollapsed ? "rotate-180" : ""
          )} />
        </Button>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header (Mobile) */}
        <header className="lg:hidden flex items-center justify-between h-14 px-4 border-b bg-background">
          <div className="flex items-center gap-2">
            <Scale className="h-6 w-6 text-primary" />
            <span className="font-semibold">Admin Panel</span>
          </div>
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent mobile />
            </SheetContent>
          </Sheet>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
